import React, { useState } from "react"
import {
    Link,
} from "react-router-dom"
import { useMutation, useQuery } from "react-query"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import { Alert, Button, Col, Form } from "react-bootstrap"
import TemplateSelector from "../../common/template/selection.component"
import SideBar from "../../common/layout/sidebar.component"
import LocationEditTabs from "./LocationTabsComponent"
import { DefaultTemplateAttributes } from "../../state/location/Location"
import LocationService from "../../state/location/LocationService"
import { DOC_TEMPLATE, EMAIL_TEMPLATE, isOfTypeEmailTemplate } from "../../state/template/TemplateType"
import { RouteComponentProps } from "react-router"
import LoadingSpinner from "../../common/spinner/loading.component"

const docTemplateMapping: Record<string, DOC_TEMPLATE | EMAIL_TEMPLATE> = {
    defaultQuoteTemplateId: DOC_TEMPLATE.QUOTE_TEMPLATE,
    defaultInvoiceTemplateId: DOC_TEMPLATE.INVOICE_TEMPLATE,
    defaultOrderTemplateId: DOC_TEMPLATE.ORDER_TEMPLATE,
    defaultContractTemplateId: DOC_TEMPLATE.CONTRACT_TEMPLATE,
    defaultQuoteEmailTemplateId: EMAIL_TEMPLATE.TYPE_EMAIL_QUOTE,
    defaultInvoiceEmailTemplateId: EMAIL_TEMPLATE.TYPE_EMAIL_INVOICE,
    defaultOrderEmailTemplateId: EMAIL_TEMPLATE.TYPE_EMAIL_ORDER,
    defaultContractEmailTemplateId: EMAIL_TEMPLATE.TYPE_EMAIL_CONTRACT,
}

type Params = {
    locationId: string,
}

const messages = defineMessages({
    [DOC_TEMPLATE.QUOTE_TEMPLATE]: {
        id: "location.form.edit.default_quote_template_label",
        defaultMessage: "Default quote template",
    },
    [DOC_TEMPLATE.INVOICE_TEMPLATE]: {
        id: "location.form.edit.default_invoice_template_label",
        defaultMessage: "Default invoice template",
    },
    [DOC_TEMPLATE.ORDER_TEMPLATE]: {
        id: "location.form.edit.default_order_template_label",
        defaultMessage: "Default order template",
    },
    [DOC_TEMPLATE.CONTRACT_TEMPLATE]: {
        id: "location.form.edit.default_contract_template_label",
        defaultMessage: "Default contract template",
    },
    [EMAIL_TEMPLATE.TYPE_EMAIL_QUOTE]: {
        id: "location.form.edit.default_quote_email_template_label",
        defaultMessage: "Default quote email template",
    },
    [EMAIL_TEMPLATE.TYPE_EMAIL_INVOICE]: {
        id: "location.form.edit.default_invoice_email_template_label",
        defaultMessage: "Default invoice email template",
    },
    [EMAIL_TEMPLATE.TYPE_EMAIL_ORDER]: {
        id: "location.form.edit.default_order_email_template_label",
        defaultMessage: "Default order email template",
    },
    [EMAIL_TEMPLATE.TYPE_EMAIL_CONTRACT]: {
        id: "location.form.edit.default_contract_email_template_label",
        defaultMessage: "Default contract email template",
    },
})

const EditLocationTemplateForm = (props: RouteComponentProps<Params>) => {

    const locationId = props.match.params.locationId
    SideBar.activateMenu("account")

    const getLocation = (): Promise<DefaultTemplateAttributes> => {
        return LocationService.getLocationById(locationId).then(r => r.getDefaultTemplates())
    }

    const [form, setForm] = useState<DefaultTemplateAttributes>({} as DefaultTemplateAttributes)
    const { data: defaultTemplates, isLoading, error } = useQuery("GET_LOCATION", {
        queryFn: getLocation,
        onSuccess: setForm,
    })

    const updateLocation = async () => {
        return await LocationService.update(locationId, form)
    }

    const mutate = useMutation(updateLocation)

    if (!defaultTemplates || error) return <></>
    if (!defaultTemplates && isLoading) return <LoadingSpinner />

    if (mutate.isSuccess) {
        setTimeout(() => {
            mutate.reset()
        }, 5000)
    }

    const getHeaders = (documentType: EMAIL_TEMPLATE | DOC_TEMPLATE): JSX.Element => {
        switch (documentType) {
        case EMAIL_TEMPLATE.TYPE_EMAIL_QUOTE:
            return <h4 className="mt-4 mb-3">
                <FormattedMessage
                    id="location.templates.default_email_template_title"
                    defaultMessage="Default Email Templates"
                />
            </h4>
        case DOC_TEMPLATE.QUOTE_TEMPLATE:
            return <h4 className="mt-2 mb-3">
                <FormattedMessage
                    id="location.templates.default_doc_template_title"
                    defaultMessage="Default Document Templates"
                />
            </h4>
        }
        return <></>
    }

    return (
        <>
            <LocationEditTabs locationId={locationId} />
            <Form>
                <h2 className="mb-4">
                    <FormattedMessage
                        id="location.edit_location_template_form.header"
                        defaultMessage="Edit Default Templates"
                    />
                </h2>
                {!isLoading && Object.values(form).length > 0 &&
                    <div className='col-12 ui-module'>
                        {Object.keys(form).map((key: string) => {
                            const documentType = docTemplateMapping[key]
                            if (!docTemplateMapping) {
                                return <></>
                            }
                            return (
                                <Form.Group className="mb-3" controlId={documentType} key={key} >
                                    {getHeaders(documentType)}
                                    <Form.Label>
                                        <FormattedMessage{...messages[documentType]} />
                                    </Form.Label>
                                    <TemplateSelector
                                        documentType={documentType}
                                        value={form[key]}
                                        onChange={(value: string) => {
                                            setForm(prevState => ({
                                                ...prevState,
                                                [key]: value
                                            }))
                                        }}
                                        className='form-control mb-2'
                                        isEmailTemplate={isOfTypeEmailTemplate(documentType)}
                                    />
                                </Form.Group>
                            )
                        })}
                    </div>
                }
                {
                    mutate.isSuccess &&
                    <Alert variant="success">
                        <FormattedMessage
                            id="location.templates.success_message"
                            defaultMessage="You have successfully updated this location's default templates!"
                        />
                    </Alert>
                }
                < div className="col-12 ui-module" >
                    <Col className='d-flex justify-content-between'>
                        <Link to='/locations/listing' className='btn btn-light'>
                            <FormattedMessage
                                id='location.form.edit.cancel_btn'
                                defaultMessage='Cancel'
                            />
                        </Link>
                        <Button variant="primary" onClick={() => mutate.mutate()}>
                            <FormattedMessage
                                id="location.form.edit.save_btn"
                                defaultMessage="Save"
                            />
                        </Button>
                    </Col>
                </div>
            </Form >
        </>
    )
}

export default injectIntl(EditLocationTemplateForm)


// WEBPACK FOOTER //
// ./src/location/form/EditLocationTemplateForm.tsx